// src/pages/ItemAdjustment/index.js
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { App } from "antd";
import { useForm } from "antd/es/form/Form";
import { useNavigate, useParams } from "react-router-dom";
import { postData } from "../../apiservice/apis"
import CreateItemAdjustmentForm from "./components/CreateItemAdjustmentForm";
import {
  selectAdjustmentVoucherNo,
  selectAdjustmentItems,
  selectAdjustmentFooter,
} from "./itemAdjustmentSelectors";
import {
  fetchAdjustmentVoucherNo,
  createItemAdjustment,
  fetchBatchesForItem,
  fetchStockForItem,
  fetchBatchSerials,
} from "./itemAdjustmentThunk";
import { setFormField, setResetAdjustment, updateRow } from "./itemAdjustmentSlice";
import { fetchItems } from "../../store/commonThunks/lookups";

import "./index.scss";
import { getAccessType } from "../../lib/functions";

const ItemAdjustment = () => {
  const { message, modal } = App.useApp();
  const dispatch = useDispatch();
  const [form] = useForm();
  const navigate = useNavigate();

  const voucherNo = useSelector(selectAdjustmentVoucherNo);
  const itemsList = useSelector(selectAdjustmentItems);
  const footerValues = useSelector(selectAdjustmentFooter);

  const { id } = useParams();


  useEffect(() => {
    if (!id) {
      dispatch(fetchAdjustmentVoucherNo());
    }
    dispatch(fetchItems());
  }, [dispatch, id]);


  //edit
  useEffect(() => {
    if (id) {
      postData("/inventory/get-adjustment-by-id", { adjust_id: id })
        .then((res) => {
          const { master, details } = res.data;

          if (!master || !details?.length) return;

          dispatch(
            setFormField({
              field: "voucherNo",
              value: master.voucher_no,
            })
          );

          // Set entry date
          dispatch(
            setFormField({
              field: "entryDate",
              value: master.created_date,
            })
          );

          dispatch(
            setFormField({
              field: "isTatmeenProduct",
              value: Boolean(master.is_tatmeen_product),
            })
          );

          // Map items properly
          const mappedItems = details.map((d, index) => ({
            key: `a-${index + 1}`,
            item_id: d.item_id,
            product: d.item_name,   // <-- now correct
            quantity: Number(d.adjust_qty),
            rate: Number(d.adjust_rate),
            total: Number(d.item_total),
            batch_id: d.batch_id || null,
            adjustment_type: d.adjust_type || "add_stock",
            available_qty: Number(d.item_qty || 0),
            loose_qty: Number(d.item_qty || 0), // Fallback
            current_qty: 0, // Will be fetched
            batch_controlled_qty: 0,
            has_batch_serial: false,
            // ✅ CRITICAL: Restore serial_mode
            serial_mode: d.serial_mode || "unit",
            // ✅ CRITICAL: Restore serials as array
            serials: d.serials
              ? d.serials.split(",").filter(Boolean)
              : [],
            // Additional fields needed for UI
            item_rate: Number(d.item_rate || 0),
            item_qty: Number(d.item_qty || 0),
            retail_qty: Number(d.retail_qty || 0),
            per_unit_id: d.per_unit_id || 0,
          }));

          dispatch(
            setFormField({
              field: "itemsList",
              value: mappedItems,
            })
          );

          dispatch(
            setFormField({
              field: "footerValues",
              value: {
                total: Number(master.total_amount),
                narration: master.narration || "",
              },
            })
          );

          dispatch(
            setFormField({
              field: "a_id",
              value: id,
            })
          );

          // ✅ Fetch batches and serials for ALL items when editing
          details.forEach((d) => {
            const itemId = d.item_id;
            const batchId = d.batch_id;

            // ✅ Fetch batches for this item (always needed)
            dispatch(
              fetchBatchesForItem({
                itemId: itemId,
                warehouseId: d.warehouse_id || 0,
                isEdit: true,
                adjustId: id,
              })
            ).then((result) => {
              // ✅ After batches are loaded, populate the batch name in the dropdown
              const batchesData = result?.payload?.data || [];
              const matchedBatch = batchesData.find(b => b.id === batchId);

              if (matchedBatch) {
                // ✅ Update the row with batch_name for display
                dispatch(
                  updateRow({
                    key: `a-${details.indexOf(d) + 1}`,
                    data: {
                      batch_name: matchedBatch.batch_name,
                      available_qty: matchedBatch.quantity ,
                      rate: matchedBatch.rate || d.adjust_rate,
                      serial_mode: matchedBatch.serial_mode || d.serial_mode || "unit",
                    }
                  })
                );
              }
            });

            // ✅ Fetch stock info
            dispatch(
              fetchStockForItem({
                itemId: itemId,
                warehouseId: d.warehouse_id || 0,
              })
            );

            // ✅ If Tatmeen and unit mode or batch mode, fetch serials
            if (
              master.is_tatmeen_product &&
              batchId
            ) {
              dispatch(
                fetchBatchSerials({
                  itemId: itemId,
                  batchId: batchId,
                  isEdit: true,
                })
              );
            }
          });
        });
    }
  }, [id, dispatch]);



  const handleSave = () => {
    if (!itemsList.length) {
      message.warning("Please add at least one item!");
      return;
    }
    // You can put more validation (qty > 0 etc.)
    dispatch(createItemAdjustment())
      .unwrap()
      .then(() => {
        modal.success({
          title: id
            ? "Item Adjustment Updated"
            : "Item Adjustment Saved",
        });

        dispatch(setResetAdjustment());
        form.resetFields();
        dispatch(
          setFormField({
            field: "itemsList",
            value: [
              {}
            ]
          })
        );

        dispatch(setResetAdjustment());
        dispatch(fetchAdjustmentVoucherNo());
        if (id) {
          navigate('/inventory/item-adjustment');
        }

      })
      .catch((err) => {
        if (err?.details?.length > 0) {
          // Insufficient stock error from backend
          modal.error({
            title: "Insufficient Stock",
            content: (
              <div>
                <p>{err.message}</p>
                <ul style={{ marginTop: 8, paddingLeft: 16 }}>
                  {err.details.map((item, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>
                      <strong>{item.item_name}</strong> — Available:{" "}
                      <span style={{ color: "red" }}>{item.available}</span>,
                      Requested: {item.requested}
                    </li>
                  ))}
                </ul>
              </div>
            ),
          });
        } else {
          modal.error({
            title: "Save Failed",
            content: err?.message || "Something went wrong. Please try again.",
          });
        }
      });
  };

  return (
    <CreateItemAdjustmentForm
      form={form}
      onSave={handleSave}
      voucherNo={voucherNo}
      itemsList={itemsList}
      footerValues={footerValues}
    />
  );
};

export default ItemAdjustment;
